import { TFunction } from "i18next";
import { getPersonValidations, nameValidator } from "@/utils/validations/person";
import { getExecutivePeopleValidations } from "@/utils/validations/company";
import { CustomDate } from "@/types/schemas/person";
import { ExecutivePeopleFormValues } from "@/screens/Customer/ExecutivePeople/form";
import { executiveRoleId } from "@/constants/executivePerson";
import { Process, Segment, Contract } from "@/types/contract";
import { uploadState } from "@/constants/attachment";
import { getAttachmentValidator } from "@/utils/validations/attachment";

const getPersonToValidateIndex = (values: ExecutivePeopleFormValues) => {
  return values.executivePeople.findIndex(e => e.id === values.currentPersonId);
};

const shouldValidate = (values: ExecutivePeopleFormValues, path: string, endPath: string) => {
  const personIndex = getPersonToValidateIndex(values);
  return path === `executivePeople.${personIndex}.${endPath}`;
};

const shouldValidateUploaded = (values: ExecutivePeopleFormValues) => {
  const personIndex = getPersonToValidateIndex(values);
  return (
    values.executivePeople[personIndex]?.executiveRoleId !== executiveRoleId && !!values.executivePeople[personIndex]?.editable
  );
};

export const validationsExecutivePeople = (t: TFunction<"validations">, contract: Contract) => {
  const validateContacts = contract.process === Process.VRB || contract.segment === Segment.B2B;

  const personValidations = getPersonValidations(t);
  const ExecutivePeopleValidations = getExecutivePeopleValidations(t);
  const attachmentValidator = getAttachmentValidator(t);

  return {
    executivePeople: {
      name: (value: string, values: ExecutivePeopleFormValues, path: string) =>
        shouldValidate(values, path, "name") && nameValidator(20, t("invalidName"), t("executivePersonNameInvalid"), value),
      surname: (value: string, values: ExecutivePeopleFormValues, path: string) =>
        shouldValidate(values, path, "surname") && nameValidator(40, t("invalidSurname"), t("executivePersonNameInvalid"), value),
      birthDate: (value: CustomDate, values: ExecutivePeopleFormValues, path: string) =>
        shouldValidate(values, path, "birthDate") &&
        values.executivePeople[getPersonToValidateIndex(values)]?.executiveRoleId !== executiveRoleId &&
        !!values.executivePeople[getPersonToValidateIndex(values)]?.editable &&
        personValidations.birthDate(value),
      executiveRoleId: (value: string | null, values: ExecutivePeopleFormValues, path: string) =>
        shouldValidate(values, path, "executiveRoleId") && ExecutivePeopleValidations.executiveRoleId(value),
      email: (value: string, values: ExecutivePeopleFormValues, path: string) =>
        validateContacts && shouldValidate(values, path, "email") && personValidations.email(value),
      mobilePhone: (value: string, values: ExecutivePeopleFormValues, path: string) =>
        validateContacts &&
        shouldValidate(values, path, "mobilePhone") &&
        personValidations.mobilePhone(value, values.executivePeople[getPersonToValidateIndex(values)]?.mobileCountryCode ?? ""),
    },
    uploaded: (value: uploadState, values: ExecutivePeopleFormValues) => {
      if (!shouldValidateUploaded(values)) {
        return null;
      }
      return attachmentValidator.uploaded(value, true);
    },
  };
};
